import { Link } from "react-router-dom";
import SignedImage from "@/components/media/SignedImage";
import { formatDistanceToNow } from "date-fns";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useConversations } from "@/lib/messaging/useConversations";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Sparkles } from "lucide-react";

const MessagesInner = () => {
  const { user } = useAuth();
  const { conversations, loading } = useConversations();

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Messages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your matches and ongoing conversations.
          </p>
        </div>
        <Link
          to="/raela"
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-accent transition"
        >
          <Sparkles className="h-3.5 w-3.5" /> Chat with RAELA
        </Link>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : conversations.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border p-12 text-center">
          <p className="text-base font-medium">No conversations yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            When you and a match both like each other, you can start a chat from{" "}
            <Link to="/matches" className="underline">
              Matches
            </Link>
            .
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {conversations.map((c) => {
            const cover = c.other?.photos?.[0];
            const when = c.last_message_at
              ? formatDistanceToNow(new Date(c.last_message_at), { addSuffix: true })
              : "no messages yet";
            return (
              <li key={c.id}>
                <Link
                  to={`/messages/${c.id}`}
                  className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-card hover:bg-accent/40 transition"
                >
                  <div className="relative h-14 w-14 flex-none overflow-hidden rounded-xl bg-muted">
                    {cover && (
                      <SignedImage
                        src={cover}
                        alt={c.other?.display_name ?? "Match"}
                        className="h-full w-full object-cover"
                      />
                    )}
                    {c.unread && (
                      <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-primary ring-2 ring-card" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate font-medium">
                        {c.other?.display_name ?? "Member"}
                      </p>
                      <span className="text-xs text-muted-foreground">{when}</span>
                    </div>
                    <p
                      className={`mt-0.5 truncate text-sm ${
                        c.unread ? "text-foreground font-medium" : "text-muted-foreground"
                      }`}
                    >
                      {c.preview ??
                        (c.member_a === user?.id || c.member_b === user?.id
                          ? "Say hello — match made."
                          : "")}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
};

const Messages = () => (
  <OnboardingGate>
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <MessagesInner />
      <SiteFooter />
    </div>
  </OnboardingGate>
);

export default Messages;
